import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories, getMangaByCategory } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find(c => c.slug === slug);
  const mangaList = getMangaByCategory(slug || '');

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryName = category.name;
  const pageTitle = `${categoryName}漫画-漫画全集下拉式免费阅读-土豪漫画网`;
  const pageDesc = `土豪漫画网${categoryName}漫画分类，提供${categoryName}漫画全集下拉式免费阅读，收录${category.count}部优质${categoryName}漫画作品，随时随地畅享中文漫画阅读服务。`;
  const pageKeywords = `${categoryName}漫画,漫画全集下拉式免费阅读,土豪漫画网`;

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '分类', path: '/category/shaonian-rexue' },
    { label: categoryName },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://i2p1d.com/" },
      { "@type": "ListItem", "position": 2, "name": "分类", "item": "http://i2p1d.com/category/shaonian-rexue" },
      { "@type": "ListItem", "position": 3, "name": categoryName },
    ]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": pageDesc,
    "url": `http://i2p1d.com/category/${slug}`,
    "isPartOf": { "@type": "WebSite", "name": "土豪漫画网", "url": "http://i2p1d.com/" },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://i2p1d.com/" },
        { "@type": "ListItem", "position": 2, "name": "分类", "item": "http://i2p1d.com/category/shaonian-rexue" },
        { "@type": "ListItem", "position": 3, "name": categoryName },
      ]
    }
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`http://i2p1d.com/category/${slug}`}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />

      <Navbar />

      <main className="pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100">
                <i className={`${category.icon} text-xl text-primary-600 w-5 h-5 flex items-center justify-center`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">{categoryName}漫画</h1>
                <p className="text-foreground-500 text-sm">{category.description} · 共{category.count}部作品</p>
              </div>
            </div>
          </div>

          {mangaList.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {mangaList.map((manga) => (
                <Link
                  key={manga.id}
                  to={`/manga/${manga.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={manga.cover}
                      alt={`${manga.title}漫画封面`}
                      title={manga.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {manga.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-1">{manga.author}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-foreground-500">{manga.latestChapter}</span>
                      <span className="flex items-center text-xs text-primary-600">
                        <i className="ri-star-fill mr-0.5 text-xs"></i>{manga.rating}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类下暂无漫画作品</p>
            </div>
          )}

          <div className="mt-10">
            <h3 className="text-lg font-bold text-foreground-900 mb-4">浏览其他分类</h3>
            <div className="flex flex-wrap gap-2">
              {categories.filter(c => c.slug !== slug).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="inline-flex items-center gap-1.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm px-4 py-2 rounded-full transition-colors whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}